import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReplies, markRead, draftReply, sendReply, detectTimeline, scheduleFollowup, getSettings, correctText } from '../api';

export default function Replies({ showToast, onRead }) {
  const [replies, setReplies] = useState([]);
  const [open,    setOpen]    = useState({});
  const [drafts,  setDrafts]  = useState({});
  const [replying,setReply]   = useState({});
  const [bodies,  setBodies]  = useState({});
  const [sending, setSend]    = useState({});
  const [loading, setLoad]    = useState(true);
  const [timelines,   setTimelines]   = useState({});   // reply_id → {requested,date,label} | "loading" | "dismissed"
  const [scheduling,  setScheduling]  = useState({});   // reply_id → bool
  const [correcting,  setCorrecting]  = useState({});   // reply_id → bool
  const [sendingEmail, setSendingEmail] = useState('');
  const [forwardTo,    setForwardTo]    = useState('');
  const nav = useNavigate();

  useEffect(() => {
    getSettings().then(r => {
      setSendingEmail(r.data.sending_email || '');
      setForwardTo(r.data.forward_to || '');
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    getReplies().then(r=>setReplies(r.data)).finally(()=>setLoad(false));
  }, []);

  const toggle = async r => {
    const opening = !open[r.id];
    setOpen(p=>({...p,[r.id]:opening}));
    if (!r.read) {
      await markRead(r.id);
      setReplies(p=>p.map(x=>x.id===r.id?{...x,read:true}:x));
      onRead && onRead();
    }
    // Auto-detect follow-up timeline when first expanding a reply
    if (opening && !timelines[r.id]) {
      setTimelines(p=>({...p,[r.id]:'loading'}));
      try {
        const res = await detectTimeline(r.id);
        setTimelines(p=>({...p,[r.id]: res.data.requested ? res.data : null}));
      } catch { setTimelines(p=>({...p,[r.id]:null})); }
    }
  };

  const handleSchedule = async (r, date) => {
    setScheduling(p=>({...p,[r.id]:true}));
    try {
      await scheduleFollowup(r.id, date);
      showToast && showToast(`Follow-up scheduled for ${date}`);
      setTimelines(p=>({...p,[r.id]:'dismissed'}));
    } catch(e) { showToast && showToast('Failed to schedule follow-up','error'); }
    finally { setScheduling(p=>({...p,[r.id]:false})); }
  };

  const handleDraft = async r => {
    setDrafts(p=>({...p,[r.id]:'loading'}));
    const res = await draftReply(r.id);
    setDrafts(p=>({...p,[r.id]:res.data.draft}));
    setBodies(p=>({...p,[r.id]:res.data.draft}));
    setReply(p=>({...p,[r.id]:true}));
  };

  const handleSend = async r => {
    setSend(p=>({...p,[r.id]:true}));
    try {
      await sendReply({ reply_id:r.id, subject:'Re: '+r.subject, body:bodies[r.id]||'' });
      showToast('Reply sent!');
      setReply(p=>({...p,[r.id]:false}));
    } catch(e) { showToast('Send failed','error'); }
    finally { setSend(p=>({...p,[r.id]:false})); }
  };

  const handleCorrect = async r => {
    const text = (bodies[r.id]||'').trim();
    if (!text) return;
    setCorrecting(p=>({...p,[r.id]:true}));
    try {
      const res = await correctText(text);
      setBodies(p=>({...p,[r.id]:res.data.corrected||text}));
    } catch(e) { showToast && showToast('Correction failed','error'); }
    finally { setCorrecting(p=>({...p,[r.id]:false})); }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  const unread = replies.filter(r=>!r.read).length;

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:13,color:'var(--text-2)'}}>
          {unread>0
            ? <><span className="pill pill-red" style={{marginRight:8}}>{unread} unread</span>{forwardTo?`All replies auto-forwarded to ${forwardTo}`:'All replies auto-forwarded'}</>
            : 'All caught up — no unread replies'}
        </div>
      </div>

      {replies.length===0 && (
        <div className="empty-state">
          <div className="empty-icon">↩</div>
          <div className="empty-title">No replies yet</div>
          <div className="empty-desc">When leads reply to your emails, they'll appear here automatically and be forwarded to your Gmail.</div>
        </div>
      )}

      {replies.map(r=>(
        <div key={r.id} className="card" style={{marginBottom:10}}>
          {/* Header row */}
          <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}
            onClick={()=>toggle(r)}>
            <div style={{width:8,height:8,borderRadius:'50%',background:r.read?'transparent':'var(--red)',flexShrink:0}}/>
            <div className="avatar av-sm av-blue" style={{flexShrink:0}}>
              {r.lead_name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:r.read?500:700,fontSize:13}}>{r.lead_name} — {r.company}</div>
              <div style={{fontSize:12,color:'var(--text-2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.subject}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
              <span style={{fontSize:11,color:'var(--text-2)'}}>{new Date(r.received_at).toLocaleDateString('en-IN')}</span>
              {!r.read && <span className="pill pill-red" style={{fontSize:10}}>New</span>}
              {r.forwarded && <span className="pill pill-green" style={{fontSize:10}}>Forwarded ✓</span>}
              <span style={{color:'var(--text-3)',fontSize:13,transition:'transform .2s',
                display:'inline-block',transform:open[r.id]?'rotate(180deg)':''}}>▾</span>
            </div>
          </div>

          {/* Expanded thread */}
          {open[r.id] && (
            <div style={{padding:'0 16px 16px',borderTop:'1px solid var(--border)'}}>
              {/* Their reply */}
              <div className="bubble bubble-in" style={{marginTop:12}}>
                <div className="bubble-hdr">
                  <span className="bubble-name" style={{color:'var(--green)'}}>{r.lead_name} replied</span>
                  <span className="bubble-time">
                    {new Date(r.received_at).toLocaleString('en-IN')}
                    {r.forwarded && <span className="pill pill-green" style={{marginLeft:6,fontSize:10}}>Auto-forwarded to you</span>}
                  </span>
                </div>
                <div style={{fontSize:13,lineHeight:1.7,whiteSpace:'pre-wrap'}}>{r.body}</div>
              </div>

              {/* Timeline detection banner */}
              {timelines[r.id] && timelines[r.id] !== 'loading' && timelines[r.id] !== 'dismissed' && (
                <div style={{marginTop:10,padding:'10px 14px',borderRadius:'var(--r-lg)',
                  background:'var(--amber-bg)',border:'1px solid var(--amber)',
                  display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span style={{fontSize:14}}>📅</span>
                  <span style={{fontSize:13,flex:1,minWidth:180,color:'var(--text)'}}>
                    <strong>{r.lead_name}</strong> asked to be followed up <strong>{timelines[r.id].label}</strong>
                    {timelines[r.id].date && ` (${timelines[r.id].date})`}
                  </span>
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <button className="btn btn-sm btn-primary" disabled={scheduling[r.id]}
                      onClick={()=>handleSchedule(r, timelines[r.id].date)}>
                      {scheduling[r.id]?'Scheduling…':'✓ Schedule it'}
                    </button>
                    <button className="btn btn-sm"
                      onClick={()=>setTimelines(p=>({...p,[r.id]:'dismissed'}))}>
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {timelines[r.id]==='loading' && (
                <div style={{marginTop:8,fontSize:12,color:'var(--text-2)',display:'flex',alignItems:'center',gap:6}}>
                  <span className="spinner spinner-sm"/> Checking for scheduled follow-up request…
                </div>
              )}

              {/* Reply composer */}
              {replying[r.id] && (
                <div style={{marginTop:10,border:'1px solid var(--border)',borderRadius:'var(--r-lg)',overflow:'hidden'}}>
                  <div style={{padding:'8px 12px',background:'var(--surface2)',borderBottom:'1px solid var(--border)',
                    fontSize:12,fontWeight:600}}>
                    Reply to {r.lead_name}
                  </div>
                  <textarea className="form-textarea"
                    value={bodies[r.id]||''} onChange={e=>setBodies(p=>({...p,[r.id]:e.target.value}))}
                    style={{border:'none',borderRadius:0,minHeight:180,padding:'12px 14px',fontSize:13,lineHeight:1.7}}
                    placeholder="Type your reply…"/>
                  <div style={{padding:'8px 12px',background:'var(--surface2)',borderTop:'1px solid var(--border)',
                    display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:11,color:'var(--text-2)'}}>From {sendingEmail}</span>
                    <div style={{display:'flex',gap:7}}>
                      <button className="btn btn-sm" disabled={correcting[r.id]||!(bodies[r.id]||'').trim()}
                        onClick={()=>handleCorrect(r)}>
                        {correcting[r.id]?<><span className="spinner spinner-sm"/>Correcting…</>:'✎ Correct'}
                      </button>
                      <button className="btn btn-sm" onClick={()=>setReply(p=>({...p,[r.id]:false}))}>Cancel</button>
                      <button className="btn btn-primary btn-sm" disabled={sending[r.id]||!bodies[r.id]}
                        onClick={()=>handleSend(r)}>
                        {sending[r.id]?'Sending…':'✈ Send reply'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {!replying[r.id] && (
                <div style={{display:'flex',gap:8,marginTop:10}}>
                  <button className="btn btn-primary btn-sm" onClick={()=>handleDraft(r)}
                    disabled={drafts[r.id]==='loading'}>
                    {drafts[r.id]==='loading'
                      ? <><span className="spinner spinner-sm"/>Drafting…</>
                      : '✦ Draft reply with AI'}
                  </button>
                  <button className="btn btn-sm" onClick={()=>setReply(p=>({...p,[r.id]:true}))}>
                    ↩ Write reply
                  </button>
                  <button className="btn btn-sm" onClick={()=>nav(`/email-manager?compose=1&to=${r.lead_email}&name=${encodeURIComponent(r.lead_name)}`)}>
                    ✉ Open in Email
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
